/* eslint-disable */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import axiosInstance, { fetcher, patchFetcher, postFetcher } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';

/* ─── DESIGN TOKENS ──────────────────────────────────── */
const T = {
  radius:   '14px',
  radiusSm: '8px',
  radiusXs: '6px',

  // Option node (amber)
  optionBg:      '#fffbeb',
  optionBorder:  '#fcd34d',
  optionAccent:  '#d97706',
  optionTag:     '#fef3c7',
  optionTagTxt:  '#92400e',
  optionStripe:  '#f59e0b',

  // Variant node (indigo)
  variantBg:     '#f5f3ff',
  variantBorder: '#a5b4fc',
  variantAccent: '#4f46e5',
  variantTag:    '#ede9fe',
  variantTagTxt: '#3730a3',
  variantStripe: '#6366f1',

  // Regular node (slate)
  regularBg:     '#f8fafc',
  regularBorder: '#cbd5e1',
  regularAccent: '#475569',
  regularTag:    '#f1f5f9',
  regularTagTxt: '#334155',
  regularStripe: '#64748b',

  savedBg:    '#dcfce7',
  savedTxt:   '#15803d',
  unsavedBg:  '#fef9c3',
  unsavedTxt: '#854d0e',
};

const palette = (isOption, isVariant) =>
  isOption  ? { bg: T.optionBg,  border: T.optionBorder,  accent: T.optionAccent,  tag: T.optionTag,  tagTxt: T.optionTagTxt,  stripe: T.optionStripe  }
: isVariant ? { bg: T.variantBg, border: T.variantBorder, accent: T.variantAccent, tag: T.variantTag, tagTxt: T.variantTagTxt, stripe: T.variantStripe }
            : { bg: T.regularBg, border: T.regularBorder, accent: T.regularAccent, tag: T.regularTag, tagTxt: T.regularTagTxt, stripe: T.regularStripe };

/* ─── SMALL HELPERS ──────────────────────────────────── */
const Label = ({ children, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
    {icon && <Iconify icon={icon} width={11} sx={{ color: '#94a3b8' }} />}
    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
      {children}
    </Typography>
  </Box>
);

const SectionBox = ({ children, sx = {} }) => (
  <Box sx={{
    borderRadius: T.radiusSm,
    border: '1px solid #e2e8f0',
    p: 1.5,
    bgcolor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    ...sx
  }}>
    {children}
  </Box>
);

const EmptyState = ({ icon, text, sub }) => (
  <Box sx={{ py: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
      <Iconify icon={icon} width={20} sx={{ color: '#94a3b8' }} />
    </Box>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>{text}</Typography>
    {sub && <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>{sub}</Typography>}
  </Box>
);

/* ─── ADD ALLOCABLE VARIANT DIALOG ──────────────────── */
const AddAllocableVariantDialog = ({ open, onClose, rawMaterialId, onAdded }) => {
  const [schema,     setSchema]     = useState(null);
  const [fetching,   setFetching]   = useState(false);
  const [attributes, setAttributes] = useState({});
  const [saving,     setSaving]     = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const visibleParams = useMemo(
    () => (schema?.category?.parameters || []).filter(p => p.inventoryAddition && !p.aggregate),
    [schema]
  );

  useEffect(() => {
    if (!open || !rawMaterialId) return;
    setSchema(null);
    setAttributes({});
    setFetching(true);
    axiosInstance
      .get(`products-app/product-variant/get-or-create/${rawMaterialId}/`)
      .then(res => {
        setSchema(res.data);
        setAttributes(res.data?.attributes || {});
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar('Failed to load variant schema', { variant: 'error' });
      })
      .finally(() => setFetching(false));
  }, [open, rawMaterialId, enqueueSnackbar]);

  const handleClose = () => { setSchema(null); setAttributes({}); onClose(); };
  const setAttr = (key, val) => setAttributes(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    const missing = visibleParams.filter(p => p.required && !String(attributes[p.key] ?? '').trim());
    if (missing.length > 0) {
      enqueueSnackbar(`Please fill: ${missing.map(p => p.key).join(', ')}`, { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`products-app/product-variant/get-or-create/${rawMaterialId}/`, { material_id: rawMaterialId, attributes });
      enqueueSnackbar('Allocable variant saved', { variant: 'success' });
      if (onAdded) onAdded(res.data);
      handleClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to save variant', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (param) => {
    const { key, type, options = [], unit, required } = param;
    const label = `${key}${unit ? ` (${unit})` : ''}${required ? ' *' : ''}`;
    const val   = attributes[key] ?? '';

    if (type === 'radio' && options.length > 0) {
      return (
        <Box key={key}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', mb: 0.75 }}>{label}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {options.map(opt => {
              const selected = val === opt;
              return (
                <Box key={opt} onClick={() => setAttr(key, opt)} sx={{
                  px: 1.5, py: 0.5, borderRadius: '50px',
                  border: `1.5px solid ${selected ? T.variantAccent : '#e2e8f0'}`,
                  bgcolor: selected ? T.variantTag : '#fff',
                  color: selected ? T.variantAccent : '#64748b',
                  fontSize: '12px', fontWeight: selected ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
                  '&:hover': { borderColor: T.variantAccent, bgcolor: T.variantTag },
                }}>
                  {opt}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    return (
      <TextField key={key} label={label} size="small" fullWidth
        type={type === 'number' ? 'number' : 'text'}
        value={val} onChange={e => setAttr(key, e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,0.15)' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: T.variantAccent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="mdi:package-variant-plus" width={20} sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>Add Allocable Variant</Typography>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.1 }}>
              Material ID: <span style={{ fontFamily: 'monospace', color: T.variantAccent }}>{rawMaterialId}</span>
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        {fetching ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1.5 }}>
            <CircularProgress size={32} sx={{ color: T.variantAccent }} />
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>Loading variant schema…</Typography>
          </Box>
        ) : !schema ? (
          <EmptyState icon="mdi:alert-circle-outline" text="Failed to load schema" sub="Please try again" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: T.variantTag, border: `1px solid ${T.variantBorder}`, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Name', value: schema.name },
                { label: 'SKU', value: schema.product_sku, mono: true },
                schema.category?.name && { label: 'Category', value: schema.category.name },
              ].filter(Boolean).map(({ label, value, mono }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.25 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: T.variantTagTxt, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</Typography>
                </Box>
              ))}
            </Box>

            {visibleParams.length === 0
              ? <EmptyState icon="mdi:cog-outline" text="No configurable fields" sub="This variant has no additional parameters" />
              : visibleParams.map(param => renderField(param))
            }
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small"
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" size="small"
          disabled={saving || fetching || !schema}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check" width={15} />}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, bgcolor: T.variantAccent, boxShadow: 'none', '&:hover': { bgcolor: '#4338ca', boxShadow: 'none' } }}>
          {saving ? 'Saving…' : 'Save Variant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};




const InlineOrderList = ({ items, onChange, columns = 1 }) => {
  const move = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange(next);
  };

  return (
    <Box sx={{ mt: 1.25 }}>
      {/* Section header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1, py: 0.6, mb: 0.75,
        bgcolor: T.optionTag,
        border: `1px solid ${T.optionBorder}`,
        borderRadius: '7px',
      }}>
        <Iconify icon="mdi:sort" width={12} sx={{ color: T.optionAccent }} />
        <Typography sx={{
          fontSize: '10px', fontWeight: 700,
          color: T.optionTagTxt, letterSpacing: '0.5px', textTransform: 'uppercase',
        }}>
          Execution order
        </Typography>
      </Box>

      {/* One row per selected option */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 0.75 }}>
        {items.map((opt, idx) => (
          <Box
            key={opt.id ?? opt.value}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.55,
              borderRadius: '8px',
              border: `1.5px solid ${T.optionBorder}`,
              bgcolor: T.optionBg,
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: `0 2px 8px ${T.optionAccent}22` },
            }}
          >
            {/* Position badge */}
            <Box sx={{
              width: 20, height: 20, borderRadius: '5px',
              bgcolor: T.optionAccent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {idx + 1}
              </Typography>
            </Box>

            {/* Label */}
            <Typography sx={{
              flex: 1, fontSize: '12px', fontWeight: 600, color: '#1e293b',
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {opt.label}
            </Typography>

            {/* Cost chip */}
            {opt.cost && (
              <Chip
                label={`₹${opt.cost}`}
                size="small"
                sx={{
                  height: 16, fontSize: '10px', flexShrink: 0,
                  bgcolor: T.optionTag, color: T.optionTagTxt, borderRadius: '4px',
                }}
              />
            )}

            {/* ▲ ▼ buttons stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
           
                <span>
                  <IconButton
                    size="small"
                    disabled={idx === 0}
                    onClick={() => move(idx, idx - 1)}
                    sx={{
                      width: 18, height: 18, p: 0, borderRadius: '4px',
                      color: idx === 0 ? '#d1d5db' : T.optionAccent,
                      bgcolor: idx === 0 ? 'transparent' : T.optionTag,
                      '&:hover:not(:disabled)': { bgcolor: '#fde68a' },
                      transition: 'all 0.12s',
                    }}
                  >
                    <Iconify icon="mdi:chevron-up" width={14} />
                  </IconButton>
                </span>
            
                <span>
                  <IconButton
                    size="small"
                    disabled={idx === items.length - 1}
                    onClick={() => move(idx, idx + 1)}
                    sx={{
                      width: 18, height: 18, p: 0, borderRadius: '4px',
                      color: idx === items.length - 1 ? '#d1d5db' : T.optionAccent,
                      bgcolor: idx === items.length - 1 ? 'transparent' : T.optionTag,
                      '&:hover:not(:disabled)': { bgcolor: '#fde68a' },
                      transition: 'all 0.12s',
                    }}
                  >
                    <Iconify icon="mdi:chevron-down" width={14} />
                  </IconButton>
                </span>
            
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ─── NODE CARD ─────────────────────────────────────── */
const NodeCard = React.memo(({
  node,
  apiNodeData,
  isLoading,
  isOptionNode,
  hasRawMaterial,
  nodeOptions,
  selectedOptions,
  onOptionChange,
  variantOptions,
  allocableVariantOptions,
  selectedVariants,
  selectedAllocableVariants,
  onVariantChange,
  onAllocableVariantChange,
  rawMaterialsList,
  rawMaterialNames,
  combinedVariantIds,
  onAllocableVariantAdded,
  orderedOptions,
  onOrderChange,
}) => {
  const nodeId = node.id;
  const [addDialogRmId, setAddDialogRmId] = useState(null);
  const p = palette(isOptionNode, hasRawMaterial);
  const isUnsaved = !apiNodeData?.updated && !node.data?.updated;
  const hasVariants = combinedVariantIds.length > 0;
  const hasOptions = (selectedOptions || []).length > 0;
  const typeLabel = isOptionNode ? 'Option' : hasRawMaterial ? 'Variant' : 'Regular';
  const typeIcon = isOptionNode ? 'mdi:format-list-checks' : hasRawMaterial ? 'mdi:package-variant' : 'mdi:cube-outline';
  const optionCount = nodeOptions?.options?.length || 0;
  const optionColumns = optionCount > 6 ? 3 : optionCount > 3 ? 2 : 1;
  const nodeLabel = apiNodeData?.name || node.data?.label || node.data?.name || 'Unnamed Node';

// Add after the state declarations in NodeCard
// Merge orderedOptions with current selection, appending any newly checked items at the end
const currentOrdered = useMemo(() => {
  const ordered = orderedOptions || [];
  const selIds = selectedOptions || [];
  const cfg = nodeOptions || { options: [] };

  if (ordered.length > 0) {
    const base = ordered.filter(o => selIds.includes(o.id ?? o.value));
    const inBase = new Set(base.map(o => o.id ?? o.value));
    const newItems = selIds
      .filter(id => !inBase.has(id))
      .map(id => cfg.options.find(o => (o.id ?? o.value) === id))
      .filter(Boolean);
    return [...base, ...newItems];
  }
  return (cfg.options || []).filter(o => selIds.includes(o.id ?? o.value));
}, [orderedOptions, selectedOptions, nodeOptions]);

const showOrderList = (selectedOptions || []).length >= 2;

const handleCheckboxChange = (optId, checked) => {
  onOptionChange(nodeId, optId, checked, false);
};

const handleInlineReorder = (newItems) => {
  const newIds = newItems.map(o => o.id ?? o.value);
  onOrderChange(nodeId, newIds, newItems);
};
  return (
    <Box sx={{
      flex: '1 1 320px',
      maxWidth: 400,
      minWidth: 300,
      borderRadius: T.radius,
      border: `1.5px solid ${p.border}`,
      bgcolor: p.bg,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 10px 32px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
    }}>

      {/* Accent stripe */}
      <Box sx={{ height: 3, bgcolor: p.stripe, flexShrink: 0 }} />

      {/* Header */}
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5, borderBottom: `1px solid ${p.border}`, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: p.accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, mt: 0.25,
          boxShadow: `0 2px 8px ${p.accent}40`,
        }}>
          <Iconify icon={typeIcon} width={17} sx={{ color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={nodeLabel} placement="top">
            <Typography sx={{
              fontWeight: 700, fontSize: '13.5px', lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: '#0f172a',
            }}>
              {nodeLabel}
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.6, mt: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={typeLabel} size="small" sx={{
              height: 18, fontSize: '10px', fontWeight: 700,
              bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px',
            }} />
            {isUnsaved
              ? <Chip label="Unsaved" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.unsavedBg, color: T.unsavedTxt, borderRadius: '5px' }} />
              : <Chip
                  icon={<Iconify icon="mdi:check-circle" width={10} sx={{ color: T.savedTxt, ml: '4px !important' }} />}
                  label="Saved" size="small"
                  sx={{ height: 18, fontSize: '10px', bgcolor: T.savedBg, color: T.savedTxt, borderRadius: '5px' }}
                />
            }
          </Box>
        </Box>
        {isLoading && <CircularProgress size={16} sx={{ flexShrink: 0, mt: 0.75, color: p.accent }} />}
      </Box>

      {/* Body */}
      <Box sx={{ px: 2, py: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
            <CircularProgress size={26} sx={{ color: p.accent }} />
            <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>Loading node data…</Typography>
          </Box>

        ) : !apiNodeData ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState icon="mdi:alert-circle-outline" text="Unable to load data" sub="Check your connection and try again" />
          </Box>

        ) : (
          <>
            {/* Option node */}
            {isOptionNode && nodeOptions?.options?.length > 0 && (
              <SectionBox>
                <Label icon="mdi:format-list-checks">Select options</Label>
                <FormControl component="fieldset" fullWidth>
                  {nodeOptions?.selectionType === 'radio' ? (
                    <RadioGroup
                      value={selectedOptions?.[0] || ''}
                      onChange={(e) => onOptionChange(nodeId, e.target.value, true, true)}
                    >
                      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${optionColumns}, minmax(0, 1fr))`, gap: 0.75 }}>
                        {nodeOptions?.options?.map((opt) => (
                          <FormControlLabel
                            key={opt.id || opt.value}
                            value={opt.id || opt.value}
                            control={<Radio size="small" sx={{ color: p.accent, '&.Mui-checked': { color: p.accent }, py: 0.5 }} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#334155' }}>{opt.label}</Typography>
                                {opt.cost && (
                                  <Chip label={`₹${opt.cost}`} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px' }} />
                                )}
                              </Box>
                            }
                            sx={{ width: '100%' }}
                          />
                        ))}
                      </Box>
                    </RadioGroup>
                  ) : (
                    /* ── CHECKBOX + inline order list ── */
                    <Box>
                      <FormGroup sx={{ display: 'grid', gridTemplateColumns: `repeat(${optionColumns}, minmax(0, 1fr))`, gap: 0.75 }}>
                        {nodeOptions?.options?.map((opt) => {
                          const optId = opt.id || opt.value;
                          const checked = (selectedOptions || []).includes(optId);
                          return (
                            <FormControlLabel
                              key={optId}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={(e) => handleCheckboxChange(optId, e.target.checked)}
                                  sx={{ color: p.accent, '&.Mui-checked': { color: p.accent }, py: 0.5 }}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography sx={{ fontSize: '12px', color: '#334155' }}>{opt.label}</Typography>
                                  {opt.cost && (
                                    <Chip label={`₹${opt.cost}`} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px' }} />
                                  )}
                                </Box>
                              }
                              sx={{ width: '100%' }}
                            />
                          );
                        })}
                      </FormGroup>

                      {/* Inline order list — appears automatically when ≥2 selected */}
                      {showOrderList && (
                        <InlineOrderList
                          items={currentOrdered}
                          onChange={handleInlineReorder}
                          columns={optionColumns}
                        />
                      )}
                    </Box>
                  )}
                </FormControl>
              </SectionBox>
            )}

            {/* Variant node — material sections using fetched rawMaterialsList */}
            {hasRawMaterial && rawMaterialsList && rawMaterialsList.length > 0 && rawMaterialsList.map((rmId, idx) => {
              const hasAllocableOptions = (allocableVariantOptions[rmId] || []).length > 0;
              const hasVariantOptions = (variantOptions[rmId] || []).length > 0;
              const materialName = rawMaterialNames[rmId] || `Material ${idx + 1}`;
              
              return (
                <SectionBox key={rmId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: p.accent }} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: p.tagTxt, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      {materialName}
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: p.border }} />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {/* Regular variant - from first API */}
                    {hasVariantOptions && (
                      <Box>
                        <Label icon="mdi:tag-outline">Regular Variant</Label>
                        <Autocomplete
                          options={variantOptions[rmId] || []}
                          getOptionLabel={(opt) => opt?.name ? `${opt.name} (₹${opt.calculated_cost ?? '?'})` : ''}
                          value={selectedVariants[rmId]?.[0] || null}
                          onChange={(e, val) => onVariantChange(nodeId, rmId, val)}
                          renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Search & select variant…"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: T.radiusSm, bgcolor: '#fff', fontSize: '12px' } }}
                            />
                          )}
                        />
                      </Box>
                    )}

                    {/* Allocable variants - from second API */}
                    {hasAllocableOptions && (
                      <>
                        {hasVariantOptions && <Divider sx={{ my: 0.25 }} />}
                        <Box>
                          <Label icon="mdi:layers-outline">Allocable Variants</Label>
                          <Autocomplete
                            multiple
                            options={allocableVariantOptions[rmId] || []}
                            getOptionLabel={(opt) => opt?.name ? `${opt.name} (₹${opt.calculated_cost?.toFixed(2) ?? '—'})` : ''}
                            value={selectedAllocableVariants[rmId] || []}
                            onChange={(e, val) => onAllocableVariantChange(nodeId, rmId, val)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Search & select allocable variants…"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: T.radiusSm, bgcolor: '#fff', fontSize: '12px' } }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((opt, idx) => (
                                <Chip key={opt.id} label={opt.name} size="small" {...getTagProps({ index: idx })}
                                  sx={{ bgcolor: p.tag, color: p.tagTxt, fontSize: '10px', borderRadius: '5px', height: 20,'&:hover': { bgcolor: '#ddd6fe' } }}
                                />
                              ))
                            }
                          />
                          <Button
                            size="small" variant="outlined"
                            startIcon={<Iconify icon="mdi:plus" width={13} />}
                            onClick={() => setAddDialogRmId(rmId)}
                            sx={{
                              mt: 1, borderRadius: T.radiusSm, textTransform: 'none',
                              fontSize: '11px', fontWeight: 600,
                              color: T.variantAccent, borderColor: T.variantBorder,
                              bgcolor: T.variantTag,
                              '&:hover': { bgcolor: '#ddd6fe', borderColor: T.variantAccent },
                              py: 0.4, px: 1.25,
                            }}
                          >
                            Add New Allocable Variant
                          </Button>
                        </Box>
                      </>
                    )}
                    
                    {/* No variants available message */}
                    {!hasVariantOptions && !hasAllocableOptions && (
                      <Box sx={{ 
                        p: 1.5, 
                        textAlign: 'center',
                        bgcolor: '#f8fafc',
                        borderRadius: T.radiusSm,
                        border: '1px dashed #cbd5e1'
                      }}>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                          No variants available for this material
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </SectionBox>
              );
            })}

            {/* Regular node */}
            {!isOptionNode && !hasRawMaterial && apiNodeData && (
              <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                borderRadius: T.radiusSm, border: '1.5px dashed #e2e8f0',
                py: 3, gap: 0.75,
              }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Iconify icon="mdi:check-circle-outline" width={20} sx={{ color: '#94a3b8' }} />
                </Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8' }}>No Action Required</Typography>
                <Typography sx={{ fontSize: '10px', color: '#cbd5e1' }}>This node is automatically handled</Typography>
              </Box>
            )}

            {/* Ready banner */}
            {(hasVariants || hasOptions) && (
              <Box sx={{
                mt: 'auto', borderRadius: T.radiusSm,
                bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
                px: 1.5, py: 1,
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
              }}>
                <Iconify icon="mdi:check-circle" width={14} sx={{ color: '#16a34a' }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#15803d' }}>Ready to save</Typography>
                {hasVariants && (
                  <Chip label={`${combinedVariantIds.length} variant${combinedVariantIds.length !== 1 ? 's' : ''}`}
                    size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.variantTag, color: T.variantTagTxt, borderRadius: '5px', ml: 'auto' }} />
                )}
                {hasOptions && (
                  <Chip label={`${(selectedOptions || []).length} option${(selectedOptions || []).length !== 1 ? 's' : ''}`}
                    size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.optionTag, color: T.optionTagTxt, borderRadius: '5px' }} />
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Dialog per card */}
      {addDialogRmId && (
        <AddAllocableVariantDialog
          open={Boolean(addDialogRmId)}
          rawMaterialId={addDialogRmId}
          onClose={() => setAddDialogRmId(null)}
          onAdded={(postResponse) => {
            if (onAllocableVariantAdded) onAllocableVariantAdded(addDialogRmId, postResponse, nodeId);
          }}
        />
      )}
    </Box>
  );
}, (prev, next) => (
  prev.node === next.node &&
  prev.apiNodeData === next.apiNodeData &&
  prev.isLoading === next.isLoading &&
  prev.isOptionNode === next.isOptionNode &&
  prev.hasRawMaterial === next.hasRawMaterial &&
  prev.nodeOptions === next.nodeOptions &&
  prev.selectedOptions === next.selectedOptions &&
  prev.variantOptions === next.variantOptions &&
  prev.allocableVariantOptions === next.allocableVariantOptions &&
  prev.selectedVariants === next.selectedVariants &&
  prev.selectedAllocableVariants === next.selectedAllocableVariants &&
  prev.rawMaterialsList === next.rawMaterialsList &&
  prev.rawMaterialNames === next.rawMaterialNames &&
  prev.combinedVariantIds === next.combinedVariantIds &&
  prev.orderedOptions === next.orderedOptions &&
  prev.onOptionChange === next.onOptionChange &&
  prev.onVariantChange === next.onVariantChange &&
  prev.onAllocableVariantChange === next.onAllocableVariantChange &&
  prev.onOrderChange === next.onOrderChange &&
  prev.onAllocableVariantAdded === next.onAllocableVariantAdded
));





/* ─── MAIN COMPONENT ─────────────────────────────────── */
const SmartView3rdStep = ({
  nodes,
  rfEdges,
  rfNodes,
  open,
  onClose,
  onNodeUpdate,
  allVariantIds,
  setAllVariantIds,
  setissaveworkflowready,
  setShowVariantDialog,
  finalQuantity,
  issaveworkflowready,
  setShowSaveDialog,
  workflowId,
  allocatedOutputs,
  setAllocatedOutputs,
  selectedmasterworkflow
}) => {
  console.log('rfNodes from props:', rfNodes);
  const { enqueueSnackbar } = useSnackbar();
  const [clientData, setClientData] = useState([]);

  const [loading, setLoading] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const [showWorkflowNameDialog, setShowWorkflowNameDialog] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
  
  const [nodeDataCache, setNodeDataCache] = useState({});
  const [fetchedNodeData, setFetchedNodeData] = useState({});
  const [variantOptions, setVariantOptions] = useState({});
  const [allocableVariantOptions, setAllocableVariantOptions] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedAllocableVariants, setSelectedAllocableVariants] = useState({});
  const [nodeOptions, setNodeOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});

  // Store raw materials list from fetched node data
  const [rawMaterialsByNode, setRawMaterialsByNode] = useState({});
  const [rawMaterialNames, setRawMaterialNames] = useState({});

  // Store node types from API data
  const [nodeTypesFromApi, setNodeTypesFromApi] = useState({});
  const [hasRawMaterialFromApi, setHasRawMaterialFromApi] = useState({});

  // Track if all nodes have been loaded
  const [allNodesLoaded, setAllNodesLoaded] = useState(false);
// Add this with other state declarations (around line 420)
const [orderedOptions, setOrderedOptions] = useState({});
  // Fetch node data when component opens
  const fetchedNodeIds = useRef(new Set());
  const totalNodesRef = useRef(0);
  const loadedNodesCountRef = useRef(0);
  const handleSelectClient = (newValue) => {
    setSelectedClient(newValue);
  };
  useEffect(() => {
    if (!open || !rfNodes || rfNodes.length === 0) return;

    // Guard against parent prop updates from onNodeUpdate causing repeated full refetch
    setIsInitialLoading(true);
    totalNodesRef.current = rfNodes.length;
    loadedNodesCountRef.current = 0;

    const fetchPromises = rfNodes.map(node => fetchNodeData(node));
    Promise.all(fetchPromises).then(() => {
      console.log('All nodes loaded successfully');
      setAllNodesLoaded(true);
      setIsInitialLoading(false);
    }).catch(err => {
      console.error('Error loading nodes:', err);
      setIsInitialLoading(false);
    });
  }, [open]);

  const fetchNodeData = async (node) => {
    const nodeId = node.id;
    const backendId = node.data?.backendId;
    
    if (!backendId) {
      console.warn(`No backendId for node ${nodeId}`);
      loadedNodesCountRef.current++;
      return;
    }
    
    setLoading(p => ({ ...p, [nodeId]: true }));
    try {
      const res = await fetcher([`/new/workflow-app/node/${backendId}/`]);
      console.log('Fetched node data for node', node.data?.label, ':', res);
      fetchedNodeIds.current.add(nodeId);
      setNodeDataCache(p => ({ ...p, [backendId]: res }));
      setFetchedNodeData(p => ({ ...p, [nodeId]: res }));
      
      const isOptionNode = res.type === 'option' || res.options_node !== null;
      setNodeTypesFromApi(prev => ({ ...prev, [nodeId]: isOptionNode }));
      
      const hasRawMaterial = res.input_foreign?.some(input => input.is_material === true) || false;
      setHasRawMaterialFromApi(prev => ({ ...prev, [nodeId]: hasRawMaterial }));
      
      const rawMaterialInputs = (res.input_foreign || []).filter(input => input.is_material === true);
      const rawMaterialIds = [];
      const nameMap = {};
      const preSelectedVariantIds = {};
      
      rawMaterialInputs.forEach(input => {
        const productIds = input.value?.product_ids || [];
        rawMaterialIds.push(...productIds);
        
        const productData = input.product_data || [];
        productData.forEach(product => {
          nameMap[product.id] = product.name;
        });
        
        const productVariantData = input.product_variant_data || [];
        const variantIds = productVariantData.map(v => v.id);
        
        if (variantIds.length > 0) {
          preSelectedVariantIds[productIds[0]] = variantIds;
        }
      });
      
      setRawMaterialsByNode(prev => ({
        ...prev,
        [nodeId]: [...new Set(rawMaterialIds)]
      }));
      
      setRawMaterialNames(prev => ({
        ...prev,
        ...nameMap
      }));
      
      if (isOptionNode) {
        const cfg = res.options_node || node.data?.options_config || node.data?.options || {};
        
        let selectedIds = [];
        const rawSelected = node.data?.selected_options || res.selected_options || [];
        if (Array.isArray(rawSelected)) {
          selectedIds = rawSelected.map(o => o?.id || o?.value || o).filter(Boolean);
        }
        
        setNodeOptions(p => ({ 
          ...p, 
          [nodeId]: { 
            selectionType: cfg.selectionType || 'radio', 
            options: cfg.options || [] 
          } 
        }));
        setSelectedOptions(p => ({ ...p, [nodeId]: selectedIds }));
      }
      
      if (rawMaterialIds.length > 0) {
        const perMaterialResults = await Promise.all(
          rawMaterialIds.map(id =>
            Promise.all([fetchVariants(id), fetchAllocableVariants(id)])
          )
        );

        const freshRegularByRmId = {};
        const freshAllocableByRmId = {};
        rawMaterialIds.forEach((rmId, idx) => {
          const [regularResult, allocableResult] = perMaterialResults[idx];
          freshRegularByRmId[rmId] = regularResult?.results ?? regularResult ?? [];
          freshAllocableByRmId[rmId] = Array.isArray(allocableResult) ? allocableResult : [];
        });

        const preSelectedVariantsForNode = {};
        const preSelectedAllocableForNode = {};

        rawMaterialIds.forEach(rmId => {
          const preSelectedIds = preSelectedVariantIds[rmId] || [];
          if (preSelectedIds.length === 0) return;

          const regularMap = new Map(freshRegularByRmId[rmId].map(v => [v.id, v]));
          const allocableMap = new Map(freshAllocableByRmId[rmId].map(v => [v.id, v]));

          const regularSelectedIds = preSelectedIds.filter(id => regularMap.has(id));
          const allocableSelectedIds = preSelectedIds.filter(id => allocableMap.has(id));

          if (regularSelectedIds.length > 0) {
            const v = regularMap.get(regularSelectedIds[0]);
            if (v) preSelectedVariantsForNode[rmId] = [v];
          }
          if (allocableSelectedIds.length > 0) {
            const vs = allocableSelectedIds.map(id => allocableMap.get(id)).filter(Boolean);
            if (vs.length > 0) preSelectedAllocableForNode[rmId] = vs;
          }
        });

        if (Object.keys(preSelectedVariantsForNode).length > 0) {
          setSelectedVariants(prev => ({
            ...prev,
            [nodeId]: { ...(prev[nodeId] || {}), ...preSelectedVariantsForNode },
          }));
        }
        if (Object.keys(preSelectedAllocableForNode).length > 0) {
          setSelectedAllocableVariants(prev => ({
            ...prev,
            [nodeId]: { ...(prev[nodeId] || {}), ...preSelectedAllocableForNode },
          }));
        }
      }
      
    } catch (err) {
      console.error('Error fetching node data:', err);
      enqueueSnackbar(`Failed to load ${node.data?.label}`, { variant: 'error' });
    } finally {
      setLoading(p => ({ ...p, [nodeId]: false }));
      loadedNodesCountRef.current++;
    }
  };
  const fetchClientData = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_HOST_API}/order-app/list/client/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setClientData(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error.response?.data || error.message);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, []);
  const fetchVariants = async (id) => {
    try { 
      const r = await fetcher([`/new/workflow-app/products/variants/list/${id}/`]); 
      console.log('Regular variants for product', id, ':', r);
      setVariantOptions(p => ({ ...p, [id]: r?.results || r || [] })); 
      return r;
    } catch (err) { 
      console.error('Error fetching regular variants:', err);
      setVariantOptions(p => ({ ...p, [id]: [] })); 
      return [];
    }
  };
  
  const fetchAllocableVariants = async (id) => {
    try { 
      const r = await fetcher([`/new/workflow-app/products/variants/list/is/allocable/${id}/`]); 
      console.log('Allocable variants for product', id, ':', r);
      setAllocableVariantOptions(p => ({ ...p, [id]: Array.isArray(r) ? r : [] })); 
      return r;
    } catch (err) { 
      console.error('Error fetching allocable variants:', err);
      setAllocableVariantOptions(p => ({ ...p, [id]: [] })); 
      return [];
    }
  };

  const handleAllocableVariantAdded = async (rmId, postResponse, sourceNodeId) => {
    const newVariantId = postResponse?.variant?.id;
    try {
      const r = await fetcher([`/new/workflow-app/products/variants/list/is/allocable/${rmId}/`]);
      const freshList = Array.isArray(r) ? r : [];
      setAllocableVariantOptions(p => ({ ...p, [rmId]: freshList }));
      if (newVariantId && sourceNodeId) {
        const newVariantObj = freshList.find(v => v.id === newVariantId);
        if (newVariantObj) {
          setSelectedAllocableVariants(p => ({
            ...p,
            [sourceNodeId]: { ...(p[sourceNodeId] || {}), [rmId]: [...(p[sourceNodeId]?.[rmId] || []), newVariantObj] },
          }));
        }
      }
    } catch (err) {
      console.error('Failed to refresh allocable variants:', err);
      if (postResponse?.variant) setAllocableVariantOptions(p => ({ ...p, [rmId]: [...(p[rmId] || []), postResponse.variant] }));
    }
  };

  const handleVariantChange = useCallback((nodeId, rmId, val) => {
    setSelectedVariants(p => ({ 
      ...p, 
      [nodeId]: { ...(p[nodeId] || {}), [rmId]: val ? [val] : [] } 
    }));
  }, []);

  const handleAllocableVariantChange = useCallback((nodeId, rmId, val) => {
    setSelectedAllocableVariants(p => ({ 
      ...p, 
      [nodeId]: { ...(p[nodeId] || {}), [rmId]: val || [] } 
    }));
  }, []);

  const handleOptionChange = useCallback((nodeId, optId, checked, isRadio = false) => {
    let newSelection = [];

    setSelectedOptions(prev => {
      const cur = prev[nodeId] || [];
      const sel = (isRadio || nodeOptions[nodeId]?.selectionType === 'radio')
        ? [optId]
        : checked ? [...cur, optId] : cur.filter(i => i !== optId);

      newSelection = sel;
      return { ...prev, [nodeId]: sel };
    });

    if (!checked && !isRadio && nodeOptions[nodeId]?.selectionType !== 'radio') {
      setOrderedOptions(p => ({
        ...p,
        [nodeId]: (p[nodeId] || []).filter(o => (o.id ?? o.value) !== optId),
      }));
    }

    if (onNodeUpdate) {
      const node = rfNodes.find(n => n.id === nodeId);
      if (node) {
        const cfg = nodeOptions[nodeId] || { selectionType: 'radio', options: [] };
        const updatedOptions = cfg.options.map(o => ({ ...o, selected: newSelection.includes(o.id || o.value) }));
        const selectedObjs = cfg.options.filter(o => newSelection.includes(o.id || o.value));
        onNodeUpdate(node, {
          options_config: { ...cfg, options: updatedOptions },
          options: { ...cfg, options: updatedOptions },
          selected_options: selectedObjs,
        });
      }
    }
  }, [nodeOptions, onNodeUpdate, rfNodes]);

  // Called by InlineOrderList on every ▲/▼ click
  const handleOrderChange = useCallback((nodeId, newIds, newItems) => {
    setOrderedOptions(p => ({ ...p, [nodeId]: newItems }));
    setSelectedOptions(p => ({ ...p, [nodeId]: newIds }));

    if (onNodeUpdate) {
      const node = rfNodes.find(n => n.id === nodeId);
      if (node) {
        const cfg = nodeOptions[nodeId] || { selectionType: 'checkbox', options: [] };
        const updatedOptions = cfg.options.map(o => ({ ...o, selected: newIds.includes(o.id || o.value) }));
        onNodeUpdate(node, {
          options_config: { ...cfg, options: updatedOptions },
          options: { ...cfg, options: updatedOptions },
          selected_options: newItems,
          selected_options_order: newIds,
        });
      }
    }
  }, [nodeOptions, onNodeUpdate, rfNodes]);

const getSelectedOptionObjects = (nodeId) => {
  console.log("---- getSelectedOptionObjects called ----");
  console.log("nodeId:", nodeId);

  const ordered = orderedOptions[nodeId] || [];
  const selIds = selectedOptions[nodeId] || [];
  const cfg = nodeOptions[nodeId] || { options: [] };

  console.log("orderedOptions:", ordered);
  console.log("selectedOptionIds:", selIds);
  console.log("nodeOptions:", cfg.options);

  // Helper function to check if an ID exists in selIds (comparing both string and number)
  const isIdSelected = (id) => {
    if (id === undefined || id === null) return false;
    const idStr = String(id);
    const idNum = typeof id === 'number' ? id : Number(id);
    return selIds.some(selectedId => 
      String(selectedId) === idStr || 
      (selectedId === idNum) ||
      String(selectedId) === idNum.toString()
    );
  };

  // Check if ordered options match selection
  const isOrderedValid =
    ordered.length === selIds.length &&
    ordered.every(opt => isIdSelected(opt.id ?? opt.value));

  console.log("isOrderedValid:", isOrderedValid);

  if (isOrderedValid) {
    console.log("Returning ordered options");
    return ordered;
  }

  // Fallback to unordered selection - comparing both string and number
  const filtered = cfg.options.filter(opt => {
    const optId = opt.id ?? opt.value;
    return isIdSelected(optId);
  });

  console.log("Returning filtered options:", filtered);

  return filtered;
};
  const getCombinedVariantIds = (nodeId) => {
    const reg = Object.values(selectedVariants[nodeId] || {}).flat().filter(v => v?.id).map(v => v.id);
    const alc = Object.values(selectedAllocableVariants[nodeId] || {}).flat().filter(v => v?.id).map(v => v.id);
    return [...new Set([...reg, ...alc])];
  };

const saveNode = async (node) => {
  const nodeId = node.id;
  const backendId = node.data?.backendId;
  const nodeData = fetchedNodeData[nodeId];
  
  if (!backendId || !nodeData) return { success: false };
  try {
    const isOpt = node.type === 'option' || nodeData?.type === 'option' || nodeData?.options_node;
    const ids = getCombinedVariantIds(nodeId);
    if (ids.length > 0) {
      await patchFetcher(['/new/workflow-app/product/variant/add/in/output/', { node_id: backendId, output_id: nodeData?.output_foreign?.[0]?.id || '', product_variant_ids: ids }]);
      if (setAllVariantIds) setAllVariantIds(p => [...new Set([...p, ...ids])]);
    }
    const fd = new FormData();
    fd.append('name', nodeData.name);
    if (nodeData.type) fd.append('type', nodeData.type);
    
    if (isOpt && (selectedOptions[nodeId] || []).length > 0) {
      const opts = getSelectedOptionObjects(nodeId);
      const cfg = nodeOptions[nodeId] || { selectionType: 'radio', options: [] };
      
      // Add order to selected options (for checkbox mode)
      const selectedOptionsWithOrder = nodeOptions[nodeId]?.selectionType !== 'radio' && opts.length > 0
        ? opts.map((opt, index) => ({
            id: opt.id || opt.value,
            value: opt.id || opt.value,
            label: opt.label,
            cost: opt.cost,
            order: index + 1,  // 1-based order
            ...opt
          }))
        : opts;
      
      fd.append('option_config', JSON.stringify({ 
        selectionType: cfg.selectionType, 
        options: cfg.options.map(o => ({ 
          ...o, 
          selected: opts.some(s => s.id === o.id || s.value === o.value) 
        })) 
      }));
      
      // Send selected_options with order information
      fd.append('selected_options', JSON.stringify(selectedOptionsWithOrder));
      
      // Also send the order array separately if backend expects it
      if (nodeOptions[nodeId]?.selectionType !== 'radio' && opts.length > 0) {
        fd.append('selected_options_order', JSON.stringify(opts.map(o => o.id ?? o.value)));
      }
    } else if (nodeData?.options_node) {
      fd.append('option_config', JSON.stringify(nodeData.options_node));
    }
    
    fd.append('input_foreign_data', JSON.stringify({ 
      name: nodeData.input_foreign?.[0]?.name || nodeData.name + ' Input', 
      product_ids: nodeData.input_foreign?.[0]?.value?.product_ids || [], 
      utility_ids: nodeData.input_foreign?.[0]?.value?.utility_ids || [], 
      product_variant_ids: ids 
    }));
    fd.append('output_foreign_ids', JSON.stringify(nodeData.output_foreign?.map(o => o.id) || []));
    fd.append('cost_foreign_ids', JSON.stringify(nodeData.cost_foreign?.map(c => c.id) || []));
    if (nodeData.machine) fd.append('machine_ids', JSON.stringify(nodeData.machine.map(m => m.id) || []));
    if (nodeData.users) fd.append('user_ids', JSON.stringify(nodeData.users));
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    const res = node.data?.updated
      ? await axiosInstance.patch(`/new/workflow-app/nodes/update/${backendId}/`, fd, config)
      : await axiosInstance.post('/new/workflow-app/nodes/create/', fd, config);
    if (onNodeUpdate) onNodeUpdate(node, { ...node.data, backendId: res.data.id, updated: true, label: nodeData.name });
    return { success: true };
  } catch (err) {
    console.error(err);
    return { success: false, error: err.message };
  }
};

  const handleSaveAll = async () => {
    setSavingAll(true);
    try {
      const all = rfNodes;
      if (!all.length) { enqueueSnackbar('No nodes to save', { variant: 'info' }); return; }
      enqueueSnackbar(`Saving ${all.length} nodes…`, { variant: 'info' });
      let ok = 0, fail = 0;
      for (const n of all) { const r = await saveNode(n); r.success ? ok++ : fail++; }
      if (fail === 0) { enqueueSnackbar(`All ${ok} nodes saved`, { variant: 'success' }); setissaveworkflowready(true); }
      else enqueueSnackbar(`Saved ${ok}, failed ${fail}`, { variant: 'warning' });
    } catch { enqueueSnackbar('Save failed', { variant: 'error' }); }
    finally { setSavingAll(false); }
  };

  const handleSaveWorkflow = async () => {
    setShowWorkflowNameDialog(true);
  };

const handleConfirmSaveWorkflow = async () => {
  if (!workflowName.trim()) {
    enqueueSnackbar('Please enter a workflow name', { variant: 'error' });
    return;
  }

  setSavingWorkflow(true);
  setShowWorkflowNameDialog(false);

  try {
    // Create a mapping from old frontend IDs to new backend IDs
    const idMap = {};
    rfNodes.forEach(node => {
      if (node.data.backendId) {
        idMap[node.id] = node.data.backendId.toString();
      }
    });

    // Process edges with new IDs
    const edgesToSave = rfEdges.map(edge => {
      // Get the new source and target IDs from the mapping
      const newSourceId = idMap[edge.source] || edge.source;
      const newTargetId = idMap[edge.target] || edge.target;
      
      // Generate a new edge ID based on the new source and target IDs
      // This ensures each edge has a unique ID based on its source and target
      const newEdgeId = `e-${newSourceId}-${newTargetId}-${Date.now()}`;
      
      return {
        id: newEdgeId, // Use the new generated ID
        source: newSourceId,
        target: newTargetId,
        label: edge.label,
        style: edge.style,
        data: edge.data
      };
    });

    const modelToSave = {
      nodes: rfNodes.map(node => {
        const { onOptionSelect, ...data } = node.data;

        if (node.type === 'option') {
          if (!data.selected_options || data.selected_options.length === 0) {
            delete data.selected_options;
          }
        } else {
          delete data.selected_options;
        }

        return {
          id: data.backendId?.toString() || node.id,
          type: node.type,
          position: node.position,
          data,
        };
      }),
      edges: edgesToSave, // Use the processed edges with new IDs
      viewport: { x: 0, y: 0, zoom: 1 }
    };

    const updatedAvailableNodes = nodes.map(node => ({
      backendId: node.backendId,
      id: node.backendId?.toString() || node.id,
      name: node.name,
      color: node.color || '#00c0ff',
      type: node.type,
      input: node.input_foreign || [],
      output: node.output_foreign || [],
      cost: node.cost_foreign || [],
    }));

    const newWorkflow = {
      name: workflowName,
      workflow_creation_id: workflowId,
      model: modelToSave,
      availableNodes: updatedAvailableNodes,
      allocated_outputs: allocatedOutputs || [],
      final_quantity: finalQuantity,
      process_creation_id: selectedmasterworkflow,
      client_id: selectedClient?.id
    };

    const res = await postFetcher([
      `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/save/`,
      newWorkflow,
    ]);

    enqueueSnackbar(`Workflow "${workflowName}" saved successfully!`, { variant: 'success' });
    setWorkflowName("");
    
    // Optionally trigger a callback to refresh workflows list
    if (setShowSaveDialog) {
      setShowSaveDialog(false);
    }
    
  } catch (error) {
    console.error('Error saving workflow:', error);
    enqueueSnackbar('Error saving workflow!', { variant: 'error' });
  } finally {
    setSavingWorkflow(false);
  }
};

  if (!open) return null;

  if (isInitialLoading) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#f1f5f9', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} thickness={4} sx={{ color: T.variantAccent }} />
        <Typography variant="h6" sx={{ color: '#475569' }}>Loading workflow data...</Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>Fetching nodes and variant options</Typography>
      </Box>
    );
  }

  const totalNodes = rfNodes.length;
  const savedCount = rfNodes.filter(n => n.data?.updated).length;

  return (
    <>
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#f1f5f9', zIndex: 1200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{
          flexShrink: 0, px: 3, py: 1.75,
          bgcolor: '#fff', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}>
          {/* Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.variantAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${T.variantAccent}40` }}>
              <Iconify icon="mdi:view-dashboard-variant" width={18} sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#0f172a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                Smart View - Step 3
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                {savedCount}/{totalNodes} nodes saved
              </Typography>
            </Box>
          </Box>

          {/* Progress bar */}
          <Box sx={{ flex: 1, maxWidth: 200, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ height: 6, borderRadius: '50px', bgcolor: '#e2e8f0', overflow: 'hidden' }}>
              <Box sx={{
                height: '100%', borderRadius: '50px',
                bgcolor: savedCount === totalNodes && totalNodes > 0 ? '#22c55e' : T.variantAccent,
                width: totalNodes > 0 ? `${(savedCount / totalNodes) * 100}%` : '0%',
                transition: 'width 0.4s ease',
              }} />
            </Box>
          </Box>

          {/* Node type pills */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[
              { count: rfNodes.filter(n => nodeTypesFromApi[n.id] === true).length, label: 'Option', bg: T.optionTag, color: T.optionTagTxt, icon: 'mdi:format-list-checks' },
              { count: rfNodes.filter(n => hasRawMaterialFromApi[n.id] === true).length, label: 'Variant', bg: T.variantTag, color: T.variantTagTxt, icon: 'mdi:package-variant' },
              { count: rfNodes.filter(n => !nodeTypesFromApi[n.id] && !hasRawMaterialFromApi[n.id]).length, label: 'Regular', bg: T.regularTag, color: T.regularTagTxt, icon: 'mdi:cube-outline' },
            ].map(({ count, label, bg, color, icon }) => (
              <Chip
                key={label}
                icon={<Iconify icon={icon} width={11} sx={{ color: `${color} !important`, ml: '6px !important' }} />}
                label={`${count} ${label}`}
                size="small"
                sx={{ height: 24, fontSize: '11px', fontWeight: 600, bgcolor: bg, color, borderRadius: '7px' }}
              />
            ))}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexShrink: 0 }}>
        
            <Button variant="contained" size="small" onClick={handleSaveAll}
              disabled={savingAll || rfNodes.length === 0}
              startIcon={savingAll
                ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                : <Iconify icon="mdi:content-save-all" width={14} />}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px',
                bgcolor: T.variantAccent, boxShadow: 'none',
                '&:hover': { bgcolor: '#4338ca', boxShadow: 'none' },
              }}>
              {savingAll ? 'Saving…' : 'Save All Nodes'}
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveWorkflow}
              disabled={!issaveworkflowready || savingWorkflow}
              startIcon={savingWorkflow
                ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                : <Iconify icon="mdi:check-all" width={14} />}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px',
                bgcolor: '#16a34a', boxShadow: 'none',
                '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
              }}>
              {savingWorkflow ? 'Saving…' : 'Save Workflow'}
            </Button>
            <Button variant="outlined" size="small" onClick={onClose}
              startIcon={<Iconify icon="mdi:close" width={14} />}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>
              Close
            </Button>
          </Box>
        </Box>

        {/* Card grid */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {rfNodes.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <Iconify icon="mdi:check-circle-outline" width={36} sx={{ color: '#22c55e' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#334155' }}>No nodes in workflow</Typography>
              <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>Add nodes to the canvas to get started</Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 2,
              alignItems: 'stretch',
            }}>
              {rfNodes.map((node) => {
                const nodeId = node.id;
                const isOptionNode = nodeTypesFromApi[nodeId] || false;
                const hasRawMaterial = hasRawMaterialFromApi[nodeId] || false;
                const rawMaterials = rawMaterialsByNode[nodeId] || [];
                const apiNodeData = fetchedNodeData[nodeId];
                const isLoading = loading[nodeId];
                
                return (
                  <NodeCard
                    key={nodeId}
                    node={node}
                    apiNodeData={apiNodeData}
                    isLoading={isLoading}
                    isOptionNode={isOptionNode}
                    hasRawMaterial={hasRawMaterial}
                    nodeOptions={nodeOptions[nodeId] || {}}
                    selectedOptions={selectedOptions[nodeId] || []}
                    onOptionChange={handleOptionChange}
                    variantOptions={variantOptions}
                    allocableVariantOptions={allocableVariantOptions}
                    selectedVariants={selectedVariants[nodeId] || {}}
                    selectedAllocableVariants={selectedAllocableVariants[nodeId] || {}}
                    onVariantChange={handleVariantChange}
                    onAllocableVariantChange={handleAllocableVariantChange}
                    rawMaterialsList={rawMaterials}
                    rawMaterialNames={rawMaterialNames}
                    combinedVariantIds={getCombinedVariantIds(nodeId)}
                    onAllocableVariantAdded={handleAllocableVariantAdded}
                    orderedOptions={orderedOptions[nodeId] || []}
                    onOrderChange={handleOrderChange}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Workflow Name Dialog */}
      <Dialog
        open={showWorkflowNameDialog}
        onClose={() => setShowWorkflowNameDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Save Workflow</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter a name for your workflow
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Name"
            type="text"
            fullWidth
            variant="outlined"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            placeholder="Enter workflow name..."
            sx={{ mt: 1 }}
          />

           
                          <Autocomplete
                            options={clientData}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedClient}
                            onChange={(event, newValue) => handleSelectClient(newValue)}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Client"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '0.875rem',
                                    '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '1.5px' },
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
                                  '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.875rem' },
                                }}
                              />
                            )}
                          />
                        
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWorkflowNameDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSaveWorkflow} 
            variant="contained" 
            color="primary"
            disabled={!workflowName.trim() || savingWorkflow}
            startIcon={savingWorkflow ? <CircularProgress size={16} /> : null}
          >
            {savingWorkflow ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SmartView3rdStep.propTypes = {
  nodes: PropTypes.array,
  rfNodes: PropTypes.array,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onNodeUpdate: PropTypes.func,
  allVariantIds: PropTypes.array,
  setAllVariantIds: PropTypes.func,
  setissaveworkflowready: PropTypes.func,
  setShowVariantDialog: PropTypes.func,
  finalQuantity: PropTypes.object,
  issaveworkflowready: PropTypes.bool,
  setShowSaveDialog: PropTypes.func,
  workflowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocatedOutputs: PropTypes.array,
  setAllocatedOutputs: PropTypes.func,
};

export default SmartView3rdStep;